import { useCallback, useEffect, useState } from "react"
import axios from "axios"
import { API_URL } from "@/shared"

export interface LiveLocation {
  riderId: string
  riderName: string
  riderType: string
  supabaseUserId: string
  latitud: number
  longitud: number
  updatedAt: string
  saleOrderId: string
  saleOrderName?: string
  /** "delivery" (punto de entrega) o "pickup" (punto de recojo) de la parada activa de esta SO */
  action?: string
  /** true si es una posición sintética (parada confirmada, rider sin GPS todavía) ubicada en su punto de partida aproximado -- no es una posición real transmitida por el rider */
  atOrigin?: boolean
  /** Solo si el repartidor está en retorno a la oficina con un recojo: la parada, el destino (la oficina) y la ETA */
  stopId?: string
  stopCorrelativo?: string
  odooState?: string
  destinoLat?: number
  destinoLng?: number
  /** Hora estimada de llegada a la oficina (ISO) */
  etaAt?: string
  etaDistanceMeters?: number
}

const POLL_INTERVAL_MS = 5000

interface LiveLocationsResult {
  /** País con el que se pidió ("" = todos): lo de otro país no se muestra mientras llega lo nuevo */
  key: string
  locations: LiveLocation[]
  error: string | null
  lastUpdated: Date | null
}

/** `country` (solo el admin): las ubicaciones de ese país; sin él, todas (al que no es admin el back le da las de su país) */
export function useLiveLocations(country?: string) {
  const key = country ?? ""
  const [result, setResult] = useState<LiveLocationsResult | null>(null)
  const [refreshTick, setRefreshTick] = useState(0)

  useEffect(() => {
    let active = true
    const fetchLocations = async () => {
      try {
        const res = await axios.get<{ data: LiveLocation[] }>(`${API_URL}/logistics/riders/live-locations`, { params: { country: key || undefined } })
        if (active) setResult({ key, locations: Array.isArray(res.data.data) ? res.data.data : [], error: null, lastUpdated: new Date() })
      } catch {
        if (active) setResult((prev) => ({ key, locations: prev?.key === key ? prev.locations : [], error: "No se pudo obtener las ubicaciones en tiempo real", lastUpdated: prev?.key === key ? prev.lastUpdated : null }))
      }
    }
    void fetchLocations()
    const interval = setInterval(fetchLocations, POLL_INTERVAL_MS)
    return () => {
      active = false
      clearInterval(interval)
    }
  }, [key, refreshTick])

  const refetch = useCallback(() => setRefreshTick((tick) => tick + 1), [])
  const current = result?.key === key ? result : null

  return { locations: current?.locations ?? [], isLoading: !current, error: current?.error ?? null, lastUpdated: current?.lastUpdated ?? null, refetch }
}
